interface ForesightRecommendsProps {
  onTooltipShow: (title: string, content: string) => void;
}

export default function ForesightRecommends({ onTooltipShow }: ForesightRecommendsProps) {
  const premiumFeatures = [
    {
      title: 'Expert SEO Audit',
      description: 'Comprehensive site analysis by industry experts with actionable recommendations',
      icon: 'ri-search-eye-line',
      value: 'Unlock 47 expert insights'
    },
    {
      title: 'Advanced Link Building',
      description: 'Proven strategies and templates from top-performing campaigns',
      icon: 'ri-links-line',
      value: '12 advanced techniques'
    },
    {
      title: 'Content Strategy Blueprint',
      description: 'Data-driven content plans that consistently rank in top 3 positions',
      icon: 'ri-file-text-line',
      value: '8 winning frameworks'
    },
    {
      title: 'Technical SEO Mastery',
      description: 'Advanced technical optimizations for enterprise-level performance',
      icon: 'ri-code-s-slash-line',
      value: '15 technical strategies'
    }
  ];

  const expertInsights = [
    {
      expert: 'Sarah Chen',
      role: 'Technical SEO Director',
      company: 'Fortune 500 Tech',
      insight: 'Schema markup implementation increased CTR by 34%',
      locked: true
    },
    {
      expert: 'Michael Rodriguez',
      role: 'Content Strategy Lead',
      company: 'Global E-commerce',
      insight: 'Topic cluster strategy drove 156% organic growth',
      locked: true
    },
    {
      expert: 'Emily Watson',
      role: 'Link Building Expert',
      company: 'Digital Agency',
      insight: 'Broken link building secured 89 high-DA backlinks',
      locked: true
    }
  ];

  const pricingTiers = [
    {
      name: 'Silver',
      price: '$99',
      period: '/month',
      features: [
        'Basic expert recommendations',
        'Monthly strategy calls',
        'Email support',
        'Standard templates'
      ],
      current: true
    },
    {
      name: 'Gold',
      price: '$299',
      period: '/month',
      features: [
        'All Silver features',
        'Advanced expert strategies',
        'Weekly strategy calls',
        'Priority support',
        'Custom templates',
        'Competitor intelligence',
        'Advanced analytics'
      ],
      recommended: true
    },
    {
      name: 'Platinum',
      price: '$599',
      period: '/month',
      features: [
        'All Gold features',
        'Dedicated SEO strategist',
        'Daily monitoring',
        '24/7 priority support',
        'White-label reports',
        'API access',
        'Custom integrations'
      ]
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header with Lock Icon */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center">
            <i className="ri-vip-crown-line text-white text-2xl"></i>
          </div>
          <div>
            <div className="flex items-center space-x-3">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Foresight Recommends</h2>
              <span className="px-3 py-1 bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs font-bold rounded-full">
                PREMIUM
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Unlock expert strategies and advanced techniques
            </p>
          </div>
        </div>
        <button
          onClick={() => onTooltipShow('Premium Features', 'Access industry expert recommendations, advanced SEO techniques, and proven strategies that consistently deliver top rankings. Upgrade to Gold plan to unlock all features.')}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <i className="ri-information-line text-lg"></i>
        </button>
      </div>

      {/* Premium Features Grid - Locked */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {premiumFeatures.map((feature, index) => (
          <div key={index} className="relative bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 overflow-hidden">
            {/* Lock Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur-sm z-10 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-lock-line text-white text-2xl"></i>
                </div>
                <p className="text-white font-semibold mb-2">Upgrade to Gold</p>
                <p className="text-gray-300 text-sm">Unlock this premium feature</p>
              </div>
            </div>

            {/* Content (Blurred) */}
            <div className="filter blur-sm">
              <div className="flex items-start space-x-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-100 to-orange-100 dark:from-yellow-900/30 dark:to-orange-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className={`${feature.icon} text-yellow-600 dark:text-yellow-400 text-xl`}></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{feature.description}</p>
                </div>
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                <span className="text-sm font-medium text-yellow-600 dark:text-yellow-400">
                  {feature.value}
                </span>
                <button className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg text-sm font-medium whitespace-nowrap">
                  View Details
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Expert Insights - Locked Preview */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
              <i className="ri-user-star-line text-white text-lg"></i>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Industry Expert Insights</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Learn from the best in the industry</p>
            </div>
          </div>
          <span className="px-3 py-1 bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs font-bold rounded-full">
            LOCKED
          </span>
        </div>

        <div className="space-y-4">
          {expertInsights.map((insight, index) => (
            <div key={index} className="relative p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg overflow-hidden">
              {/* Lock Badge */}
              <div className="absolute top-4 right-4 w-8 h-8 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-full flex items-center justify-center z-10">
                <i className="ri-lock-line text-white text-sm"></i>
              </div>

              {/* Content (Partially Blurred) */}
              <div className="filter blur-[2px]">
                <div className="flex items-start space-x-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-line text-purple-600 dark:text-purple-400"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 dark:text-white">{insight.expert}</h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400">{insight.role} at {insight.company}</p>
                  </div>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 italic">"{insight.insight}"</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Upgrade CTA */}
      {/* Removed upgrade CTA section */}
    </div>
  );
}
